import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { services as fallbackServices, type Service } from "@/data/caseStudies";

// Static imports resolve to URL strings at build time — files are only
// fetched when used in <img src>. The ServicesSection only renders the
// active service image, so only 1-2 SVGs are loaded at a time.
import svcWebDesignLight from "@/assets/services/web-design-light.svg";
import svcWebDesignDark from "@/assets/services/web-design-dark.svg";
import svcEcommerceLight from "@/assets/services/framer-ecommerce-light.svg";
import svcEcommerceDark from "@/assets/services/framer-ecommerce-dark.svg";
import svcWebsiteFixLight from "@/assets/services/website-fix-light.svg";
import svcWebsiteFixDark from "@/assets/services/website-fix-dark.svg";
import svcEmailMarketingLight from "@/assets/services/email-marketing-light.svg";
import svcEmailMarketingDark from "@/assets/services/email-marketing-dark.svg";
import svcAssessmentLight from "@/assets/services/assessment-light.svg";
import svcAssessmentDark from "@/assets/services/assessment-dark.svg";

type ImagePair = { light: string; dark: string };

const imageMap: { keyword: string; images: ImagePair }[] = [
  { keyword: "Design & Development", images: { light: svcWebDesignLight, dark: svcWebDesignDark } },
  { keyword: "Landing Page", images: { light: svcWebDesignLight, dark: svcWebDesignDark } },
  { keyword: "E-Commerce", images: { light: svcEcommerceLight, dark: svcEcommerceDark } },
  { keyword: "Fix", images: { light: svcWebsiteFixLight, dark: svcWebsiteFixDark } },
  { keyword: "Email", images: { light: svcEmailMarketingLight, dark: svcEmailMarketingDark } },
  { keyword: "Assessment", images: { light: svcAssessmentLight, dark: svcAssessmentDark } },
];

function resolveImages(title: string): { image: string; imageDark?: string } {
  const match = imageMap.find((m) => title.includes(m.keyword));
  if (match) return { image: match.images.light, imageDark: match.images.dark };
  return { image: svcWebDesignLight, imageDark: svcWebDesignDark };
}

export function useServices(): { services: Service[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("services")
        .select("title, description, pricing, details, display_order")
        .order("display_order");
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 1,
    refetchOnWindowFocus: true,
  });

  if (!data) {
    return { services: fallbackServices, isLoading };
  }

  const mapped: Service[] = data.map((row) => ({
    title: row.title,
    description: row.description,
    pricing: row.pricing,
    details: row.details ?? undefined,
    ...resolveImages(row.title),
  }));

  return { services: mapped, isLoading: false };
}
